import type { EconHeaderCountdownEvent } from "../types/econ-header";

const RELEASED_ACTUAL_WINDOW_MS = 20 * 60 * 1000;
const AWAITING_ACTUAL_WINDOW_MS = 20 * 1000;
const PRE_COUNTDOWN_MS = 5 * 60 * 1000;
const PULSE_SECONDS = new Set([
  300, 240, 180, 120, 60, 30, 15, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1,
]);

export type EconHeaderCountdownPulseMode = "none" | "threshold" | "awaiting";

export function hasEconHeaderActual(
  value: string | number | null | undefined,
): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  return typeof value === "string" && value.trim().length > 0;
}

export function econHeaderActualPrintKey(
  event: Pick<
    EconHeaderCountdownEvent,
    "dayPlanWindowId" | "actualValue" | "actualPrintedAt"
  >,
): string | null {
  if (!hasEconHeaderActual(event.actualValue)) return null;
  return `${event.dayPlanWindowId}:${event.actualPrintedAt ?? String(event.actualValue)}`;
}

export function shouldRevealEconHeaderPrint(input: {
  actualPrintKey: string | null;
  revealedPrintKey: string | null;
  forceExpanded: boolean;
}): boolean {
  return Boolean(
    input.actualPrintKey &&
    (input.forceExpanded || input.revealedPrintKey !== input.actualPrintKey),
  );
}

export function getEconHeaderCountdownCandidate(
  events: EconHeaderCountdownEvent[],
  now = Date.now(),
): EconHeaderCountdownEvent | null {
  let best: EconHeaderCountdownEvent | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const event of events) {
    const scheduled = new Date(event.scheduledAt).getTime();
    const delta = scheduled - now;
    const hasActual = hasEconHeaderActual(event.actualValue);
    const actualPrintedAt = event.actualPrintedAt
      ? new Date(event.actualPrintedAt).getTime()
      : null;
    const actualAge =
      actualPrintedAt && Number.isFinite(actualPrintedAt)
        ? now - actualPrintedAt
        : null;
    const isFreshPrintedActual =
      hasActual &&
      actualAge != null &&
      actualAge >= 0 &&
      actualAge <= RELEASED_ACTUAL_WINDOW_MS;
    const isInCountdown = delta <= PRE_COUNTDOWN_MS && delta > 0;
    const isAwaiting =
      !hasActual && delta <= 0 && Math.abs(delta) <= AWAITING_ACTUAL_WINDOW_MS;
    const isPrinted =
      hasActual &&
      (Math.abs(delta) <= AWAITING_ACTUAL_WINDOW_MS || isFreshPrintedActual);
    if (!isInCountdown && !isAwaiting && !isPrinted) continue;

    const distance = isFreshPrintedActual
      ? Math.min(Math.abs(delta), actualAge ?? Number.POSITIVE_INFINITY)
      : Math.abs(delta);
    if (distance >= bestDistance) continue;
    best = event;
    bestDistance = distance;
  }

  return best;
}

export function formatHeaderCountdown(
  scheduledAt: string,
  now = Date.now(),
): string {
  const delta = Math.max(0, new Date(scheduledAt).getTime() - now);
  const totalSeconds = Math.ceil(delta / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function getEconHeaderCountdownPulseMode({
  secondsRemaining,
  hasActual,
}: {
  secondsRemaining: number;
  hasActual: boolean;
}): EconHeaderCountdownPulseMode {
  if (hasActual) return "none";
  if (secondsRemaining <= 0) return "awaiting";
  return PULSE_SECONDS.has(secondsRemaining) ? "threshold" : "none";
}

export function displayHeaderValue(value: string | number | null | undefined) {
  const text = value == null ? "" : String(value).trim();
  return text || "--";
}

export function displayHeaderForecast(
  value: string | number | null | undefined,
) {
  const text = displayHeaderValue(value);
  if (text === "--") return text;

  const stance = text.match(/\b(hawkish|dovish|neutral)\b/i)?.[1];
  if (stance && !parseHeaderNumber(text)) return titleCase(stance);

  const range = displayHeaderRange(text);
  if (range) return range;

  const numeric = text.match(/-?\d+(?:\.\d+)?\s*(?:%|bps?|mm|bn|[kmb])?/i)?.[0];
  if (numeric) return numeric.replace(/\s+/g, "");
  if (stance) return titleCase(stance);

  return text.split(/\s+/)[0] ?? text;
}

function displayHeaderRange(value: string): string | null {
  const source = value.replace(/,/g, "");
  const matches = Array.from(
    source.matchAll(/[-+]?\d+(?:\.\d+)?\s*(?:%|bps?|mm|bn|[kmb])?/gi),
  ).map((match) => normalizeHeaderRangeToken(source, match));
  if (matches.length < 2) return null;
  const unit = headerUnit(matches[0]) || headerUnit(matches[1]);
  return `${ensureHeaderUnit(matches[0], unit)}-${ensureHeaderUnit(matches[1], unit)}`;
}

function normalizeHeaderRangeToken(source: string, match: RegExpMatchArray) {
  const token = match[0].replace(/\s+/g, "");
  const index = match.index ?? 0;
  if (!token.startsWith("-") || index === 0) return token;
  const previous = source[index - 1];
  return previous && !/\s|[(]/.test(previous) ? token.slice(1) : token;
}

function headerUnit(token: string) {
  return token.match(/(?:%|bps?|mm|bn|[kmb])$/i)?.[0] ?? "";
}

function ensureHeaderUnit(token: string, unit: string) {
  if (!unit || headerUnit(token)) return token;
  return `${token}${unit}`;
}

export function formatHeaderReleaseVerdict(
  event: EconHeaderCountdownEvent,
): "Beat" | "Miss" | "In Line" | "Actual" {
  const storedDirection = event.intelligence?.scoreImpact.direction;
  if (storedDirection) return verdictLabel(storedDirection);

  const actual = parseHeaderNumber(event.actualValue);
  const comparison =
    parseHeaderNumber(event.deskForecast) ??
    parseHeaderNumber(event.calendarForecast) ??
    parseHeaderNumber(event.previousValue);
  if (actual == null || comparison == null) {
    return formatStanceVerdict(event);
  }
  if (actual == null || comparison == null || comparison === 0) {
    return "Actual";
  }

  const surprise = ((actual - comparison) / Math.abs(comparison)) * 100;
  if (Math.abs(surprise) < 0.5) return "In Line";
  return surprise > 0 ? "Beat" : "Miss";
}

function formatStanceVerdict(
  event: EconHeaderCountdownEvent,
): "Beat" | "Miss" | "In Line" | "Actual" {
  const actual = parseStance(event.actualValue);
  const comparison =
    parseStance(event.deskForecast) ??
    parseStance(event.calendarForecast) ??
    parseStance(event.previousValue);
  if (actual == null || comparison == null) return "Actual";
  if (actual === comparison) return "In Line";
  return actual > comparison ? "Beat" : "Miss";
}

function verdictLabel(
  direction: NonNullable<
    EconHeaderCountdownEvent["intelligence"]
  >["scoreImpact"]["direction"],
) {
  if (direction === "beat") return "Beat";
  if (direction === "miss") return "Miss";
  return "In Line";
}

function parseHeaderNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value == null) return null;
  const raw = String(value).replace(/,/g, "").trim();
  const match = raw.match(/(-?\d+(?:\.\d+)?)\s*%?\s*([kKmM])?\b/);
  if (!match) return null;
  const suffix = match[2]?.toLowerCase();
  const multiplier = suffix === "k" ? 1_000 : suffix === "m" ? 1_000_000 : 1;
  const numeric = parseFloat(match[1]);
  return Number.isFinite(numeric) ? numeric * multiplier : null;
}

function parseStance(value: unknown): -1 | 0 | 1 | null {
  const match = String(value ?? "").match(/\b(hawkish|dovish|neutral)\b/i);
  if (!match) return null;
  const stance = match[1].toLowerCase();
  if (stance === "hawkish") return 1;
  if (stance === "dovish") return -1;
  return 0;
}

function titleCase(value: string) {
  const lower = value.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}
